/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { GuestProfileGroup, GuestProfileGroupUser } = protos;

/**
 * Add a guest group.
 *
 * @param {Object} guestGroup Guest groups
 * @returns {Object} A redux TockApi action.
 */
export function add(guestGroup: dashboard.GuestProfileGroup): ApiAction {
  const request = GuestProfileGroup.fromObject(guestGroup);

  return {
    TockApi: {
      postProto: '/guest/profile/group',
      type: AppConstants.GUEST_GROUP_ADD,
      request,
      toast: 'Guest group created',
    },
  };
}

/**
 * Get all users for a guest group.
 *
 * @param {Number} id The id of a guest group
 * @returns {Object} A redux TockApi action.
 */
export function fetchUsersForGroup(id: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/guest/profile/group/${id}/user?limit=50000`,
      type: AppConstants.GUEST_GROUP_FETCH_USERS,
    },
  };
}

/**
 * Add emails directly to a guest group.
 *
 * @param {Number} id The id of a guest group
 * @param {Array} emails An array of emails to add
 * @returns {Object} A redux TockApi action.
 */
export function addEmails(id: number, emails: string[]): ApiAction {
  const requests: any[] = [];

  _.forEach(emails, (email) => {
    const payload = GuestProfileGroupUser.fromObject({
      guestProfileGroupId: id,
      patron: {
        email,
      },
    });

    requests.push({
      path: `/guest/profile/group/${id}/user`,
      method: 'POST',
      payload,
      requestType: GuestProfileGroupUser,
    });
  });

  return {
    TockApi: {
      batchProto: requests,
      type: AppConstants.GUEST_GROUP_ADD_EMAILS,
      toast: 'Emails added',
    },
  };
}

/**
 * De-focuses the filter field when filtering guest group emails.
 *
 * @returns {Object} A redux action.
 */
export function blurFilter(): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_BLUR_FILTER,
  };
}

/**
 * Clears the filter for a guest groups emails.
 *
 * @returns {Object} A redux action.
 */
export function clearFilter(): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_CLEAR_FILTER,
  };
}

/**
 * Deselect a guest group.
 *
 * @returns {Object} A redux action.
 */
export function deselect(): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_DESELECT,
  };
}

/**
 * Fetch all guest groups.
 *
 * @returns {Object} A redux TockApi action.
 */
export function fetchAll(): ApiAction {
  return {
    TockApi: {
      getListProto: '/guest/profile/group',
      type: AppConstants.GUEST_GROUP_FETCH_ALL,
    },
  };
}

/**
 * Focuses the filter field when filtering guest group emails.
 *
 * @returns {Object} A redux action.
 */
export function focusFilter(): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_FOCUS_FILTER,
  };
}

/**
 * Archive a guest group.
 *
 * @param {Number} id The id of a guest group
 * @returns {Object} A redux TockApi action.
 */
export function archive(guestGroup: dashboard.GuestProfileGroup): ApiAction {
  const request = GuestProfileGroup.fromObject({
    ...guestGroup,
    isArchived: true,
  });

  return {
    TockApi: {
      putProto: `/guest/profile/group`,
      type: AppConstants.GUEST_GROUP_UPDATE,
      request,
      toast: 'Guest group updated',
    },
  };
}

/**
 * Remove an email from a guest group.
 *
 * @param {Number} id The id of a guest group
 * @param {String} email The email to remove
 * @returns {Object} A redux TockApi action.
 */
export function removeEmail(id: number, email: string): ApiAction {
  return {
    TockApi: {
      deleteProto: `/guest/profile/group/${id}/user?email=${encodeURIComponent(email)}`,
      type: AppConstants.GUEST_GROUP_REMOVE_EMAIL,
      toast: 'Guest removed',
    },
  };
}

/**
 * Select a guest group.
 *
 * @param {Number} id The id of a guest group
 * @returns {Object} A redux action.
 */
export function select(id: number): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_SELECT,
    payload: id,
  };
}

/**
 * Sets the filter for guest groups emails.
 *
 * @param {String} filter The text to filter by
 * @returns {Object} A redux action.
 */
export function setFilter(filter: any): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_SET_FILTER,
    payload: filter,
  };
}

/**
 * Update the given guest group.
 *
 * @param {Object} guestGroup Guest groups
 * @returns {Object} A redux TockApi action.
 */
export function update(guestGroup: dashboard.GuestProfileGroup): ApiAction {
  const request = GuestProfileGroup.fromObject(guestGroup);

  return {
    TockApi: {
      putProto: `/guest/profile/group`,
      type: AppConstants.GUEST_GROUP_UPDATE,
      request,
      toast: 'Guest group updated',
    },
  };
}

/**
 * Update a guest group a csv of emails.
 *
 * @param {Number} id The id of a guest group
 * @param {File} file The csv of emails to upload
 * @returns {Object} A redux TockApi action.
 */
export function upload(id: number, file: any): ApiAction {
  return {
    TockApi: {
      postFile: `/guest/profile/group/${id}/users`,
      type: AppConstants.GUEST_GROUP_UPDATE,
      request: file,
      toast: 'Emails uploaded successfully',
    },
  };
}

/**
 * View a page of emails
 *
 * @param {Number} pageNum The page to view
 * @returns {Object} A redux action.
 */
export function viewPage(pageNum: number): BaseTockAction {
  return {
    type: AppConstants.GUEST_GROUP_VIEW_PAGE,
    payload: pageNum,
  };
}
